import List from '@mui/material/List';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import { type ReactElement, useId } from 'react';
import { useTranslation } from 'react-i18next';

import type { Channel } from '../../../api';
import { SectionHeader } from '../../../components/SectionHeader';

import { ChannelRow } from './ChannelRow';
import { splitSections } from './channel-list';

/** Props of {@link ChannelPicker}. */
export interface ChannelPickerProps {
  readonly channels: readonly Channel[];
  /** Ids of channels with a write in flight. */
  readonly busy: ReadonlySet<string>;
  readonly onToggle: (channel: Channel) => void;
}

/**
 * The two sections of mockup 4: the affiliate's recommended channels, then all the others
 * (S5 R2, R12). Presentational, so S9 can move it to `src/components/` (Q30).
 */
export function ChannelPicker({ channels, busy, onToggle }: ChannelPickerProps): ReactElement {
  const { t } = useTranslation();
  const recommendedId = useId();
  const othersId = useId();
  const { recommended, others } = splitSections(channels);
  const section = (id: string, title: string, rows: readonly Channel[]): ReactElement | null =>
    rows.length === 0 ? null : (
      <Stack component="section" aria-labelledby={id} spacing={1}>
        <SectionHeader id={id} title={title} />
        <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
          <List disablePadding>
            {rows.map((channel) => (
              <ChannelRow
                key={channel.id}
                channel={channel}
                busy={busy.has(channel.id)}
                onToggle={onToggle}
              />
            ))}
          </List>
        </Paper>
      </Stack>
    );
  return (
    <Stack spacing={2}>
      {section(recommendedId, t('onboarding.channels.recommendedTitle'), recommended)}
      {section(othersId, t('onboarding.channels.allTitle'), others)}
    </Stack>
  );
}
